import { supabase } from '../supabaseClient';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA256 = /^[0-9a-f]{64}$/;
const REF = /^o[1-9][0-9]*$/;
const MAX_ROSTER = 300;
const MAX_SELECTED = 12;
const TRANSCRIPTIONS = new Set(['accepted', 'corrected', 'refused']);
const USAGES = new Set(['programme', 'contexte', 'avis_medical']);
const MEAL_STRUCTURES = new Set([
  'trois_repas',
  'collation_apres_midi',
  'collation_matin_apres_midi',
  'collation_soir',
]);
const READINESS_CODES = new Set([
  'ready',
  'no_active_reports',
  'reports_unprepared',
  'review_incomplete',
  'anomalies_unresolved',
  'conflicts_unresolved',
  'programme_conflict_unresolved',
  'mgd_review_not_ready',
  'revision_stale',
  'safety_context_missing',
  'policy_registry_unavailable',
  'clinical_policy_manifest_missing',
  'registry_definition_missing',
  'component_roster_missing',
  'component_payload_invalid',
  'component_catalog_missing',
  'component_roster_mismatch',
  'component_content_unvalidated',
  'clearance_execution_spec_missing',
  'component_confirmation_missing',
  'product_authorization_missing',
  'draft_roster_mismatch',
  'draft_items_unconfirmed',
  'evaluator_dsl_unconfirmed',
  'plan_contract_unconfirmed',
  'data_handling_unconfirmed',
  'clinical_policy_unsealed',
  'clinical_policy_signature_invalid',
  'clinical_policy_expired',
  'clinical_policy_incomplete',
  'clinical_policy_stale',
  'clinical_policy_roster_stale',
]);
const SAFE_CODES = new Set([
  'access_denied', 'ai_unavailable', 'anomalies_unresolved', 'conflicts_unresolved',
  'clinical_clearance_required', 'clinical_policy_expired', 'clinical_policy_incomplete',
  'clinical_policy_signature_invalid', 'clinical_policy_stale', 'clinical_policy_unsealed',
  'mgd_review_not_ready', 'mgd_revision_stale', 'not_found', 'policy_registry_unavailable',
  'mgd_output_consumed', 'mgd_output_not_ready', 'mgd_output_stale',
  'programme_conflict_unresolved', 'rate_limited', 'review_unavailable',
  'safety_context_missing', 'session_unavailable',
  'revision_stale', 'source_unavailable', 'stale_report', 'unavailable',
]);

export class MgdReviewV2Error extends Error {
  constructor(code, details = null) {
    super(code);
    this.name = 'MgdReviewV2Error';
    this.code = code;
    this.details = details;
  }
}

function fail(code = 'review_unavailable', details = null) {
  throw new MgdReviewV2Error(code, details);
}

function exact(value, fields) {
  return value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).length === fields.length
    && fields.every((field) => Object.hasOwn(value, field));
}

function boundedText(value, max, { nullable = false, empty = false } = {}) {
  if (nullable && value === null) return true;
  return typeof value === 'string' && value.length <= max && (empty || value.trim().length > 0);
}

function nonNegativeInteger(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function validateReportStatusRow(row) {
  const fields = ['anomalyCount', 'candidateCount', 'conflictCount', 'label', 'reportId',
    'reportState', 'reportType', 'reportVersion', 'reviewedCount', 'selectedCount', 'stale', 'status'];
  if (!exact(row, fields) || !UUID.test(row.reportId || '')
      || !boundedText(row.label, 160) || !['saliva', 'dna', 'other', 'blood'].includes(row.reportType)
      || !Number.isSafeInteger(row.reportVersion) || row.reportVersion < 1
      || !['uploaded', 'needs_preparation', 'needs_review', 'reviewed', 'stale', 'blocked'].includes(row.status)
      || !['uploaded', 'in_review'].includes(row.reportState)
      || !nonNegativeInteger(row.candidateCount) || row.candidateCount > MAX_ROSTER
      || !nonNegativeInteger(row.selectedCount) || row.selectedCount > MAX_SELECTED
      || row.selectedCount > row.candidateCount
      || !nonNegativeInteger(row.reviewedCount) || row.reviewedCount > row.selectedCount
      || !nonNegativeInteger(row.anomalyCount) || !nonNegativeInteger(row.conflictCount)
      || typeof row.stale !== 'boolean') fail('invalid_response');
  return Object.freeze({ ...row });
}

function validateReadiness(value) {
  const fields = ['code', 'readinessToken', 'ready', 'reports', 'revisionHash'];
  if (!exact(value, fields)) fail('invalid_response');
  if (typeof value.ready !== 'boolean') fail('invalid_response');
  if (!READINESS_CODES.has(value.code)) fail('invalid_response');
  if (!(value.revisionHash === null || SHA256.test(value.revisionHash || ''))) {
    fail('invalid_response');
  }
  if (!(value.readinessToken === null || UUID.test(value.readinessToken || ''))) {
    fail('invalid_response');
  }
  if (!Array.isArray(value.reports)) fail('invalid_response');
  if (value.reports.length > 20) fail('invalid_response');
  if (value.ready && (!value.readinessToken || !value.revisionHash)) {
    fail('invalid_response');
  }
  if (!value.ready && value.readinessToken !== null) {
    fail('invalid_response');
  }
  return Object.freeze({ ...value, reports: Object.freeze(value.reports.map(validateReportStatusRow)) });
}

function validateRosterItem(item) {
  const fields = ['contextRefs', 'marker', 'page', 'reasonCode', 'ref', 'reviewStatus',
    'section', 'suggested', 'unit', 'value'];
  if (!exact(item, fields) || !REF.test(item.ref || '')
      || !boundedText(item.marker, 500) || !boundedText(item.value, 500)
      || !boundedText(item.unit, 100, { nullable: true, empty: true })
      || !Number.isSafeInteger(item.page) || item.page < 1
      || !boundedText(item.section, 300, { nullable: true, empty: true })
      || !boundedText(item.reviewStatus, 80) || typeof item.suggested !== 'boolean'
      || !boundedText(item.reasonCode, 80, { nullable: true })
      || !Array.isArray(item.contextRefs) || item.contextRefs.length > 8
      || item.contextRefs.some((ref) => !boundedText(ref, 100))) fail('invalid_response');
  if (item.suggested !== (item.reasonCode !== null)
      || (!item.suggested && item.contextRefs.length > 0)) fail('invalid_response');
  return Object.freeze({ ...item });
}

function validateCoverage(value, candidateCount) {
  const fields = ['candidateCount', 'extractedCount', 'rate', 'unreadableCount'];
  if (!exact(value, fields) || !nonNegativeInteger(value.candidateCount)
      || value.extractedCount !== candidateCount
      || !nonNegativeInteger(value.unreadableCount)
      || value.extractedCount + value.unreadableCount !== value.candidateCount
      || !boundedText(value.rate, 20)) fail('invalid_response');
  return Object.freeze({ ...value });
}

function validateFinding(item) {
  if (!exact(item, ['code', 'findingHash', 'page', 'ref', 'refs'])) fail('invalid_response');
  // Les anomalies et conflits restent des preuves techniques opaques. Le
  // navigateur n'en déduit jamais une décision clinique.
  const safe = {};
  for (const [key, value] of Object.entries(item)) {
    if (!['code', 'findingHash', 'ref', 'page', 'refs'].includes(key)) fail('invalid_response');
    if (key === 'code' && boundedText(value, 80)) safe.code = value;
    else if (key === 'findingHash' && SHA256.test(value || '')) safe.findingHash = value;
    else if (key === 'ref' && (value === null || REF.test(value || ''))) safe.ref = value;
    else if (key === 'page' && (value === null || (Number.isSafeInteger(value) && value > 0))) safe.page = value;
    else if (key === 'refs' && Array.isArray(value) && value.length <= 20
      && value.every((ref) => REF.test(ref || ''))) safe.refs = Object.freeze([...value]);
    else fail('invalid_response');
  }
  if (!safe.code || !safe.findingHash) fail('invalid_response');
  return Object.freeze(safe);
}

function validateConflict(item) {
  if (!exact(item, ['code', 'findingHash', 'sides'])
      || !['divergent_duplicate', 'cross_report_divergent'].includes(item.code)
      || !SHA256.test(item.findingHash || '') || !Array.isArray(item.sides)
      || item.sides.length < 2 || item.sides.length > 20) fail('invalid_response');
  const sides = item.sides.map((side) => {
    const fields = ['marker', 'page', 'ref', 'reportId', 'section', 'sourceHash', 'unit', 'value'];
    if (!exact(side, fields) || !UUID.test(side.reportId || '') || !REF.test(side.ref || '')
        || !Number.isSafeInteger(side.page) || side.page < 1
        || !boundedText(side.section, 300, { nullable: true, empty: true })
        || !boundedText(side.marker, 500) || !boundedText(side.value, 500)
        || !boundedText(side.unit, 100, { nullable: true, empty: true })
        || !SHA256.test(side.sourceHash || '')) fail('invalid_response');
    return Object.freeze({ ...side });
  });
  if (new Set(sides.map((side) => `${side.value}\u0000${side.unit ?? ''}`)).size < 2) {
    fail('invalid_response');
  }
  return Object.freeze({ ...item, sides: Object.freeze(sides) });
}

export function validateMgdReviewProposal(value) {
  const fields = ['candidateCount', 'conflicts', 'coverage', 'expiresAt', 'pdfSha256',
    'persistable', 'reportId', 'reviewToken', 'roster', 'rosterHash', 'selectedCount', 'selectedRefs',
    'snapshotHash', 'sourceFormatKey', 'stale', 'technicalAnomalies', 'version'];
  if (!exact(value, fields) || value.version !== 2 || !UUID.test(value.reportId || '')
      || !UUID.test(value.reviewToken || '') || !SHA256.test(value.snapshotHash || '')
      || !SHA256.test(value.rosterHash || '')
      || !SHA256.test(value.pdfSha256 || '') || !boundedText(value.sourceFormatKey, 100)
      || !nonNegativeInteger(value.candidateCount) || value.candidateCount > MAX_ROSTER
      || !nonNegativeInteger(value.selectedCount) || value.selectedCount > MAX_SELECTED
      || value.selectedCount > value.candidateCount || !Array.isArray(value.selectedRefs)
      || value.selectedRefs.length !== value.selectedCount
      || new Set(value.selectedRefs).size !== value.selectedRefs.length
      || value.selectedRefs.some((ref) => !REF.test(ref || ''))
      || !Array.isArray(value.roster) || value.roster.length !== value.candidateCount
      || !Array.isArray(value.technicalAnomalies) || !Array.isArray(value.conflicts)
      || typeof value.stale !== 'boolean' || typeof value.persistable !== 'boolean'
      || !boundedText(value.expiresAt, 50) || Number.isNaN(Date.parse(value.expiresAt))) fail('invalid_response');
  const roster = value.roster.map(validateRosterItem);
  const rosterRefs = new Set(roster.map((item) => item.ref));
  const suggestedRefs = roster.filter((item) => item.suggested).map((item) => item.ref);
  if (suggestedRefs.length !== value.selectedCount
      || suggestedRefs.some((ref, index) => ref !== value.selectedRefs[index])
      || value.selectedRefs.some((ref) => !rosterRefs.has(ref))) fail('invalid_response');
  const anomalies = value.technicalAnomalies.map(validateFinding);
  const conflicts = value.conflicts.map(validateConflict);
  if (value.persistable && (value.stale || anomalies.length > 0 || conflicts.length > 0)) fail('invalid_response');
  return Object.freeze({ ...value, roster: Object.freeze(roster),
    selectedRefs: Object.freeze([...value.selectedRefs]),
    coverage: validateCoverage(value.coverage, value.candidateCount),
    technicalAnomalies: Object.freeze(anomalies), conflicts: Object.freeze(conflicts) });
}

export function normalizeMgdDecision(row) {
  if (!row || typeof row !== 'object' || !REF.test(row.ref || '')
      || !TRANSCRIPTIONS.has(row.transcription)) fail('invalid_decision');
  const usage = row.usage ?? null;
  const correctedValue = row.correctedValue ?? null;
  const correctedUnit = row.correctedUnit ?? null;
  if (row.transcription === 'refused') {
    if (usage !== null || correctedValue !== null || correctedUnit !== null) fail('invalid_decision');
  } else if (!USAGES.has(usage)) fail('invalid_decision');
  if (row.transcription === 'corrected') {
    if (!boundedText(correctedValue, 500)
        || !boundedText(correctedUnit, 100, { nullable: true, empty: true })) fail('invalid_decision');
  } else if (correctedValue !== null || correctedUnit !== null) fail('invalid_decision');
  return Object.freeze({ ref: row.ref, transcription: row.transcription, usage,
    correctedValue, correctedUnit });
}

export function validateMgdReviewStatus(value) {
  const fields = ['code', 'readinessToken', 'ready', 'reports', 'revisionHash', 'version'];
  if (!exact(value, fields) || value.version !== 2) fail('invalid_response');
  const readiness = validateReadiness({ code: value.code, readinessToken: value.readinessToken,
    ready: value.ready, reports: value.reports, revisionHash: value.revisionHash });
  return Object.freeze({ version: 2, ...readiness });
}

async function bearer(auth) {
  const session = await auth?.auth?.getSession?.().catch(() => null);
  const token = session?.data?.session?.access_token;
  if (!token) fail('session_unavailable');
  return token;
}

function safeError(payload, status) {
  const nested = payload?.error && typeof payload.error === 'object' ? payload.error : null;
  const rawCode = payload?.code ?? nested?.code;
  const code = SAFE_CODES.has(rawCode)
    ? rawCode : status === 401 || status === 403 ? 'access_denied' : 'review_unavailable';
  const rawMissing = payload?.missingFields ?? nested?.missingFields;
  const missingFields = code === 'safety_context_missing' && Array.isArray(rawMissing)
    ? rawMissing.filter((field) => typeof field === 'string').slice(0, 10) : null;
  return new MgdReviewV2Error(code, missingFields ? { missingFields } : null);
}

async function sha256Text(value) {
  if (!globalThis.crypto?.subtle) fail('invalid_response');
  let digest;
  try { digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)); }
  catch { fail('invalid_response'); }
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function extractClaudeText(payload, status) {
  if (payload?.stop_reason === 'max_tokens') fail('review_unavailable');
  if (!Array.isArray(payload?.content)) fail('invalid_response');
  let value = payload.content
    .filter((block) => block?.type === 'text' && typeof block.text === 'string')
    .map((block) => block.text).join('');
  if (!value.trim() && typeof payload.content[0]?.text === 'string') value = payload.content[0].text;
  if (!value.trim()) fail('invalid_response');
  const proofFields = ['clinicalEvaluation', 'contextHash', 'id', 'model', 'optionsHash',
    'planHash', 'policy', 'revisionHash', 'sourceRevisionHash'];
  const policyFields = ['clinicalRegistry', 'noBatch', 'noFiles', 'noPromptCache', 'noWebSearch',
    'provider', 'retentionPolicy', 'version'];
  const proof = payload?.generationProof;
  const evaluation = proof?.clinicalEvaluation;
  const verdict = evaluation?.verdict;
  const componentHashes = evaluation?.component_hashes;
  if (!exact(proof, proofFields) || !UUID.test(proof.id || '')
      || !SHA256.test(proof.revisionHash || '') || !SHA256.test(proof.planHash || '')
      || !SHA256.test(proof.sourceRevisionHash || '')
      || !SHA256.test(proof.contextHash || '') || !SHA256.test(proof.optionsHash || '')
      || !boundedText(proof.model, 100) || !exact(proof.policy, policyFields)
      || !Number.isSafeInteger(proof.policy.version) || proof.policy.version < 1
      || !boundedText(proof.policy.provider, 100) || !boundedText(proof.policy.retentionPolicy, 100)
      || !exact(proof.policy.clinicalRegistry, ['sha256', 'version'])
      || !Number.isSafeInteger(proof.policy.clinicalRegistry.version)
      || proof.policy.clinicalRegistry.version < 1
      || !SHA256.test(proof.policy.clinicalRegistry.sha256 || '')
      || ['noFiles', 'noBatch', 'noPromptCache', 'noWebSearch']
        .some((key) => proof.policy[key] !== true)
      || !exact(evaluation, ['component_hashes', 'manifest_sha256', 'plan_hash',
        'policy_version', 'registry_definition_sha256', 'registry_key', 'source', 'verdict'])
      || evaluation.source !== 'server_sealed_registry_v1'
      || !boundedText(evaluation.registry_key, 100)
      || !Number.isSafeInteger(evaluation.policy_version) || evaluation.policy_version < 1
      || !SHA256.test(evaluation.manifest_sha256 || '')
      || !SHA256.test(evaluation.registry_definition_sha256 || '')
      || evaluation.plan_hash !== proof.planHash
      || !componentHashes || typeof componentHashes !== 'object' || Array.isArray(componentHashes)
      || Object.keys(componentHashes).length < 1 || Object.keys(componentHashes).length > 20
      || Object.entries(componentHashes).some(([key, hash]) => !boundedText(key, 100)
        || !SHA256.test(hash || ''))
      || !exact(verdict, ['cleared', 'severity', 'violations', 'warnings'])
      || verdict.cleared !== true || verdict.severity !== 'none'
      || !Array.isArray(verdict.violations) || verdict.violations.length !== 0
      || !Array.isArray(verdict.warnings) || verdict.warnings.length !== 0) fail('invalid_response');
  if (await sha256Text(value) !== proof.planHash) fail('invalid_response');
  return Object.freeze({ text: value, generationProof: Object.freeze({ ...proof,
    policy: Object.freeze({ ...proof.policy,
      clinicalRegistry: Object.freeze({ ...proof.policy.clinicalRegistry }) }),
    clinicalEvaluation: Object.freeze({ ...evaluation,
      component_hashes: Object.freeze({ ...componentHashes }),
      verdict: Object.freeze({ ...verdict, violations: Object.freeze([]),
        warnings: Object.freeze([]) }) }) }), status });
}

async function postJson({ auth, fetchApi, endpoint, body, validate }) {
  const token = await bearer(auth);
  let response;
  try {
    response = await fetchApi(endpoint, { method: 'POST', headers: {
      Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
    }, body: JSON.stringify(body) });
  } catch { fail('review_unavailable'); }
  let payload;
  try { payload = await response.json(); } catch { fail('review_unavailable'); }
  if (!response.ok) throw safeError(payload, response.status);
  return validate(payload);
}

export function createMgdReviewV2Client({ auth = supabase, fetchApi = globalThis.fetch,
  randomUUID = () => globalThis.crypto.randomUUID() } = {}) {
  const requestId = () => {
    const value = randomUUID();
    if (!UUID.test(value || '')) fail('invalid_request');
    return value;
  };
  return Object.freeze({
    createRequestId: requestId,
    prepare(clientId, reportId, id = requestId()) {
      if (!UUID.test(clientId || '') || !UUID.test(reportId || '') || !UUID.test(id || '')) fail('invalid_request');
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/ai-review',
        body: { clientId, reportId, requestId: id }, validate: validateMgdReviewProposal });
    },
    status(clientId) {
      if (!UUID.test(clientId || '')) fail('invalid_request');
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/review-status',
        body: { clientId }, validate: validateMgdReviewStatus });
    },
    save({ clientId, reportId, requestId: id, caseRequestId, reviewToken, snapshotHash, decisions,
      rosterAcknowledgement, findingResolutions, reportDecision, exclusionReason }) {
      if (!UUID.test(clientId || '') || !UUID.test(reportId || '') || !UUID.test(id || '')
          || !UUID.test(caseRequestId || '') || !UUID.test(reviewToken || '')
          || !SHA256.test(snapshotHash || '') || !Array.isArray(decisions)
          || decisions.length > MAX_SELECTED) fail('invalid_request');
      const normalized = decisions.map(normalizeMgdDecision);
      if (!exact(rosterAcknowledgement, ['candidateCount', 'decision', 'rosterHash'])
          || !nonNegativeInteger(rosterAcknowledgement.candidateCount)
          || rosterAcknowledgement.candidateCount > MAX_ROSTER
          || rosterAcknowledgement.decision !== 'reviewed_all'
          || !SHA256.test(rosterAcknowledgement.rosterHash || '')
          || !Array.isArray(findingResolutions) || findingResolutions.length > 120
          || !['include', 'exclude'].includes(reportDecision)
          || (reportDecision === 'include' && exclusionReason !== null)
          || (reportDecision === 'exclude' && !boundedText(exclusionReason, 500))) {
        fail('invalid_request');
      }
      if (reportDecision === 'exclude'
          && normalized.some((item) => item.transcription !== 'refused')) fail('invalid_request');
      const normalizedFindings = findingResolutions.map((item) => {
        if (!SHA256.test(item?.findingHash || '') || !boundedText(item?.note, 500, { empty: true })) {
          fail('invalid_request');
        }
        if (item.kind === 'technical_ack') {
          if (!exact(item, ['decision', 'findingHash', 'kind', 'note'])
              || item.decision !== 'acknowledged') fail('invalid_request');
          return Object.freeze({ ...item, note: item.note.trim() });
        }
        if (item.kind !== 'cross_report_conflict'
            || !exact(item, ['chosen', 'correctedUnit', 'correctedValue', 'decision',
              'findingHash', 'kind', 'note'])
            || !['keep_source', 'exclude_all', 'canonical_corrected'].includes(item.decision)) {
          fail('invalid_request');
        }
        const chosen = item.chosen;
        if (item.decision === 'keep_source') {
          if (!exact(chosen, ['ref', 'reportId']) || !UUID.test(chosen.reportId || '')
              || !REF.test(chosen.ref || '') || item.correctedValue !== null
              || item.correctedUnit !== null) fail('invalid_request');
        } else if (chosen !== null) fail('invalid_request');
        if (item.decision === 'canonical_corrected') {
          if (!boundedText(item.correctedValue, 500)
              || !boundedText(item.correctedUnit, 100, { nullable: true, empty: true })) {
            fail('invalid_request');
          }
        } else if (item.correctedValue !== null || item.correctedUnit !== null) {
          fail('invalid_request');
        }
        return Object.freeze({ ...item, chosen: chosen ? Object.freeze({ ...chosen }) : null,
          note: item.note.trim() });
      });
      const validate = (value) => {
        const fields = ['readiness', 'reportId', 'reportState', 'resultVersion', 'reused',
          'targetedReviewId', 'version'];
        if (!exact(value, fields) || value.version !== 2 || value.reportId !== reportId
            || !UUID.test(value.targetedReviewId || '') || !Number.isSafeInteger(value.resultVersion)
            || value.resultVersion < 1 || typeof value.reused !== 'boolean'
            || value.reportState !== 'in_review') fail('invalid_response');
        return Object.freeze({ ...value, readiness: validateReadiness(value.readiness) });
      };
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/targeted-review',
        body: { clientId, reportId, requestId: id, caseRequestId, reviewToken,
          snapshotHash, decisions: normalized, rosterAcknowledgement,
          findingResolutions: normalizedFindings, reportDecision, exclusionReason }, validate });
    },
    async openSource({ clientId, reportId, reviewToken, findingHash = null }) {
      if (!UUID.test(clientId || '') || !UUID.test(reportId || '') || !UUID.test(reviewToken || '')) {
        fail('invalid_request');
      }
      if (!(findingHash === null || SHA256.test(findingHash || ''))) fail('invalid_request');
      const token = await bearer(auth);
      let response;
      try { response = await fetchApi('/api/mgd/source-view', { method: 'POST', headers: {
        Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
      }, body: JSON.stringify({ clientId, reportId, reviewToken, findingHash }) }); }
      catch { fail('review_unavailable'); }
      if (!response.ok) {
        let payload = null; try { payload = await response.json(); } catch { /* réponse opaque */ }
        throw safeError(payload, response.status);
      }
      if (!String(response.headers?.get?.('content-type') || '').toLowerCase().startsWith('application/pdf')) {
        fail('invalid_response');
      }
      const blob = await response.blob().catch(() => fail('review_unavailable'));
      if (!blob || blob.size <= 0) fail('invalid_response');
      return blob;
    },
    createPlanDraft({ clientId, consultationId = null, readinessToken,
      requestId: id = requestId(), mealStructure }) {
      if (!UUID.test(clientId || '') || !(consultationId === null || UUID.test(consultationId || ''))
          || !UUID.test(readinessToken || '') || !UUID.test(id || '')
          || !MEAL_STRUCTURES.has(mealStructure)) fail('invalid_request');
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/plan-draft',
        body: { clientId, consultationId, readinessToken, requestId: id,
          options: { mealStructure } }, validate: (value) => {
          const fields = ['consultationId', 'draftId', 'expiresAt', 'revisionHash', 'version'];
          if (!exact(value, fields) || value.version !== 2 || !UUID.test(value.draftId || '')
              || !(value.consultationId === null || UUID.test(value.consultationId || ''))
              || !SHA256.test(value.revisionHash || '') || !boundedText(value.expiresAt, 50)
              || Number.isNaN(Date.parse(value.expiresAt))) fail('invalid_response');
          return Object.freeze({ ...value });
        } });
    },
    async generatePlan({ clientId, draftId, readinessToken, requestId: id = requestId(),
      signal = null }) {
      if (!UUID.test(clientId || '') || !UUID.test(draftId || '')
          || !UUID.test(readinessToken || '') || !UUID.test(id || '')) fail('invalid_request');
      const token = await bearer(auth);
      let response;
      try { response = await fetchApi('/api/mgd/plan-generation', { method: 'POST', headers: {
        Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
      }, signal, body: JSON.stringify({ clientId, draftId, readinessToken,
        requestId: id }) }); } catch { fail('review_unavailable'); }
      let payload; try { payload = await response.json(); } catch { fail('review_unavailable'); }
      if (!response.ok) throw safeError(payload, response.status);
      return extractClaudeText(payload, response.status);
    },
    authorizePlanAdoption({ clientId, generationProofId, readinessToken, finalPlanText }) {
      if (!UUID.test(clientId || '') || !UUID.test(generationProofId || '')
          || !UUID.test(readinessToken || '') || !boundedText(finalPlanText, 500000)) {
        fail('invalid_request');
      }
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/authorize-plan-adoption',
        body: { clientId, generationProofId, readinessToken,
          finalPlanText: finalPlanText.replace(/\r\n/g, '\n') },
        validate: (value) => {
          const fields = ['adoptionId', 'expiresAt', 'finalPlanHash', 'revisionHash', 'version'];
          if (!exact(value, fields) || value.version !== 1 || !UUID.test(value.adoptionId || '')
              || !SHA256.test(value.finalPlanHash || '') || !SHA256.test(value.revisionHash || '')
              || !boundedText(value.expiresAt, 50) || Number.isNaN(Date.parse(value.expiresAt))) {
            fail('invalid_response');
          }
          return Object.freeze({ ...value });
        } });
    },
    planProofStatus({ clientId, consultationId }) {
      if (!UUID.test(clientId || '') || !UUID.test(consultationId || '')) fail('invalid_request');
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/plan-proof-status',
        body: { clientId, consultationId }, validate: (value) => {
          const fields = ['authorized', 'persisted', 'planHash', 'revisionHash'];
          if (!exact(value, fields) || typeof value.authorized !== 'boolean'
              || typeof value.persisted !== 'boolean'
              || !(value.planHash === null || SHA256.test(value.planHash || ''))
              || !(value.revisionHash === null || SHA256.test(value.revisionHash || ''))
              || ((value.persisted || value.authorized) && (!value.planHash || !value.revisionHash))) {
            fail('invalid_response');
          }
          return Object.freeze({ ...value });
        } });
    },
    authorizeOutput({ clientId, consultationId, requestId: id = requestId(), channel,
      planHash, artifactSha256 }) {
      if (!UUID.test(clientId || '') || !UUID.test(consultationId || '')
          || !UUID.test(id || '') || !['word_export', 'client_app_publication'].includes(channel)
          || !SHA256.test(planHash || '') || !SHA256.test(artifactSha256 || '')) {
        fail('invalid_request');
      }
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/output-authorization',
        body: { clientId, consultationId, requestId: id, channel, planHash, artifactSha256 },
        validate: (value) => {
          const fields = ['artifactSha256', 'authorizationId', 'channel', 'expiresAt',
            'planHash', 'reused', 'revisionHash', 'version'];
          if (!exact(value, fields) || value.version !== 1
              || !UUID.test(value.authorizationId || '') || value.channel !== channel
              || value.planHash !== planHash || value.artifactSha256 !== artifactSha256
              || !SHA256.test(value.revisionHash || '') || typeof value.reused !== 'boolean'
              || !boundedText(value.expiresAt, 50) || Number.isNaN(Date.parse(value.expiresAt))) {
            fail('invalid_response');
          }
          return Object.freeze({ ...value });
        } });
    },
    consumeOutput({ clientId, consultationId, authorizationId, channel,
      artifactSha256 }) {
      if (!UUID.test(clientId || '') || !UUID.test(consultationId || '')
          || !(authorizationId === null || UUID.test(authorizationId || ''))
          || channel !== 'word_export'
          || !SHA256.test(artifactSha256 || '')) fail('invalid_request');
      return postJson({ auth, fetchApi, endpoint: '/api/mgd/output-consume',
        body: { clientId, consultationId, authorizationId, channel, artifactSha256 },
        validate: (value) => {
          const fields = ['artifactSha256', 'authorizationId', 'authorized', 'channel',
            'consumed', 'persisted', 'planHash', 'required', 'revisionHash', 'version'];
          if (!exact(value, fields) || value.version !== 1
              || value.authorizationId !== authorizationId || value.channel !== channel
              || value.authorized !== true || value.persisted !== true
              || typeof value.required !== 'boolean' || value.artifactSha256 !== artifactSha256) {
            fail('invalid_response');
          }
          if (value.required) {
            if (value.consumed !== true || !UUID.test(value.authorizationId || '')
                || !SHA256.test(value.planHash || '') || !SHA256.test(value.revisionHash || '')) {
              fail('invalid_response');
            }
          } else if (value.consumed !== false || value.authorizationId !== null
              || value.planHash !== null || value.revisionHash !== null) {
            fail('invalid_response');
          }
          return Object.freeze({ ...value });
        } });
    },
  });
}

export const mgdReviewV2Client = createMgdReviewV2Client();
